// ChemistrySimulation.js
// Interactive chemistry simulations
// Visualize molecules, reactions, and chemical concepts

import React, { useState } from 'react';
import { Beaker, Droplet, Flame, Plus, RotateCcw } from 'lucide-react';

const ChemistrySimulation = ({ type, darkMode, onInteraction }) => {
  const [pH, setPH] = useState(7);
  const [temperature, setTemperature] = useState(25);
  const [concentration, setConcentration] = useState(1);
  const [reactant1Amount, setReactant1Amount] = useState(2);
  const [reactant2Amount, setReactant2Amount] = useState(2);
  const [isReacting, setIsReacting] = useState(false);

  // Removed problematic useEffect that was causing infinite re-renders

  const getPHColor = (phValue) => {
    if (phValue < 4) return '#ef4444'; // Red (Strong Acid)
    if (phValue < 6) return '#f97316'; // Orange (Weak Acid)
    if (phValue < 8) return '#22c55e'; // Green (Neutral)
    if (phValue < 11) return '#3b82f6'; // Blue (Weak Base)
    return '#8b5cf6'; // Purple (Strong Base)
  };

  const getPHLabel = (phValue) => {
    if (phValue < 4) return 'Strong Acid';
    if (phValue < 6) return 'Weak Acid';
    if (phValue < 8) return 'Neutral';
    if (phValue < 11) return 'Weak Base';
    return 'Strong Base';
  };

  const getTemperatureColor = (temp) => {
    if (temp < 10) return '#3b82f6'; // Blue (Cold)
    if (temp < 30) return '#22c55e'; // Green (Room temp)
    if (temp < 60) return '#f97316'; // Orange (Warm)
    return '#ef4444'; // Red (Hot)
  };

  const getMoleculeRepresentation = (molecule) => {
    const molecules = {
      H2O: { name: 'Water', atoms: ['H', 'O', 'H'], bonds: ['single', 'single'], color: '#3b82f6' },
      CO2: { name: 'Carbon Dioxide', atoms: ['O', 'C', 'O'], bonds: ['double', 'double'], color: '#64748b' },
      NaCl: { name: 'Sodium Chloride', atoms: ['Na', 'Cl'], bonds: ['ionic'], color: '#f8fafc' },
      CH4: { name: 'Methane', atoms: ['C', 'H', 'H', 'H', 'H'], bonds: ['single'], color: '#84cc16' },
      O2: { name: 'Oxygen', atoms: ['O', 'O'], bonds: ['double'], color: '#ef4444' },
      H2: { name: 'Hydrogen', atoms: ['H', 'H'], bonds: ['single'], color: '#fbbf24' }
    };
    return molecules[molecule] || { name: molecule, atoms: [molecule], bonds: [], color: '#9ca3af' };
  };

  const simulateReaction = () => {
    setIsReacting(true);
    setTimeout(() => {
      setIsReacting(false);
    }, 3500); // Increased to 3.5 seconds for better UX
  };

  const reset = () => {
    setPH(7);
    setTemperature(25);
    setConcentration(1);
    setReactant1Amount(2);
    setReactant2Amount(2);
    setIsReacting(false);
  };

  const getSimulationContent = () => {
    switch (type) {
      case 'ph_scale':
        return (
          <>
            <div className="mb-6">
              <div
                className="h-40 rounded-xl flex items-center justify-center text-white text-2xl font-bold transition-all duration-500 shadow-lg"
                style={{ backgroundColor: getPHColor(pH) }}
              >
                <div className="text-center">
                  <div className="text-6xl mb-2">{pH.toFixed(1)}</div>
                  <div className="text-xl opacity-90">{getPHLabel(pH)}</div>
                  <Droplet className="w-12 h-12 mx-auto mt-2 opacity-75" />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  pH Level: {pH.toFixed(1)}
                </label>
                <input
                  type="range"
                  min="0"
                  max="14"
                  step="0.1"
                  value={pH}
                  onChange={(e) => {
                    const newValue = Number(e.target.value);
                    setPH(newValue);
                    if (onInteraction) {
                      onInteraction({ type: 'ph_change', value: newValue });
                    }
                  }}
                  className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gradient-to-r from-red-500 via-green-500 to-purple-500"
                  style={{
                    WebkitAppearance: 'none',
                    outline: 'none'
                  }}
                />
                <div className="flex justify-between text-xs mt-1">
                  <span className="text-red-500">Acidic</span>
                  <span className="text-green-500">Neutral</span>
                  <span className="text-purple-500">Basic</span>
                </div>
              </div>

              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-blue-50'}`}>
                <h4 className={`font-semibold mb-2 ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                  💡 pH Scale Facts
                </h4>
                <ul className={`text-sm space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <li>• pH = -log[H⁺] (hydrogen ion concentration)</li>
                  <li>• pH &lt; 7 = Acidic, pH = 7 = Neutral, pH &gt; 7 = Basic</li>
                  <li>• Each pH unit represents a 10x change in [H⁺]</li>
                  <li>• Examples: Lemon juice ~2, Blood ~7.4, Bleach ~13</li>
                </ul>
              </div>
            </div>
          </>
        );

      case 'temperature':
        return (
          <>
            <div className="mb-6">
              <div
                className="h-40 rounded-xl flex items-center justify-center text-white text-2xl font-bold transition-all duration-500 shadow-lg relative overflow-hidden"
                style={{ backgroundColor: getTemperatureColor(temperature) }}
              >
                <div className="text-center z-10">
                  <Flame className={`w-16 h-16 mx-auto mb-2 ${temperature > 60 ? 'animate-pulse' : ''}`} />
                  <div className="text-6xl mb-2">{temperature}°C</div>
                  <div className="text-xl opacity-90">
                    {temperature < 10 ? 'Cold' : temperature < 30 ? 'Room Temperature' : temperature < 60 ? 'Warm' : 'Hot'}
                  </div>
                </div>
                {/* Particle motion simulation */}
                {temperature > 40 && (
                  <div className="absolute inset-0 opacity-30">
                    {[...Array(10)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute w-2 h-2 bg-white rounded-full animate-ping"
                        style={{
                          left: `${Math.random() * 100}%`,
                          top: `${Math.random() * 100}%`,
                          animationDuration: `${0.5 + Math.random()}s`
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Temperature: {temperature}°C
                </label>
                <input
                  type="range"
                  min="-20"
                  max="100"
                  value={temperature}
                  onChange={(e) => {
                    const newValue = Number(e.target.value);
                    setTemperature(newValue);
                    if (onInteraction) {
                      onInteraction({ type: 'temperature_change', value: newValue });
                    }
                  }}
                  className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gradient-to-r from-blue-500 via-green-500 to-red-500"
                  style={{
                    WebkitAppearance: 'none',
                    outline: 'none'
                  }}
                />
              </div>

              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-blue-50'}`}>
                <h4 className={`font-semibold mb-2 ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                  💡 Temperature & Kinetics
                </h4>
                <ul className={`text-sm space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <li>• Higher temperature = faster molecular motion</li>
                  <li>• Reaction rate doubles for every 10°C increase</li>
                  <li>• Kinetic energy = ½mv²</li>
                  <li>• Activation energy is lowered by catalysts</li>
                </ul>
              </div>
            </div>
          </>
        );

      case 'molarity':
        return (
          <>
            <div className="mb-6">
              <div className={`h-40 rounded-xl flex items-center justify-center transition-all duration-500 shadow-lg relative ${darkMode ? 'bg-gray-700' : 'bg-blue-100'}`}>
                <div className="relative w-32 h-32">
                  <Beaker className="w-full h-full text-gray-400" />
                  <div
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 w-20 transition-all duration-500 rounded-t-lg"
                    style={{
                      height: `${(concentration / 5) * 80}%`,
                      backgroundColor: `rgba(59, 130, 246, ${0.3 + (concentration / 5) * 0.7})`
                    }}
                  />
                </div>
                <div className={`absolute top-4 right-4 text-right ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  <div className="text-3xl font-bold">{concentration.toFixed(2)} M</div>
                  <div className="text-sm opacity-75">Molarity</div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Concentration: {concentration.toFixed(2)} M
                </label>
                <input
                  type="range"
                  min="0"
                  max="5"
                  step="0.1"
                  value={concentration}
                  onChange={(e) => {
                    const newValue = Number(e.target.value);
                    setConcentration(newValue);
                    if (onInteraction) {
                      onInteraction({ type: 'concentration_change', value: newValue });
                    }
                  }}
                  className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gradient-to-r from-blue-200 to-blue-600"
                  style={{
                    WebkitAppearance: 'none',
                    outline: 'none'
                  }}
                />
              </div>

              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-blue-50'}`}>
                <h4 className={`font-semibold mb-2 ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                  💡 Molarity (M) = mol/L
                </h4>
                <ul className={`text-sm space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <li>• Molarity = moles of solute / liters of solution</li>
                  <li>• Higher molarity = more concentrated solution</li>
                  <li>• Use M₁V₁ = M₂V₂ for dilutions</li>
                  <li>• Darker color = higher concentration (usually)</li>
                </ul>
              </div>
            </div>
          </>
        );

      case 'reaction':
        return (
          <>
            <div className="mb-6">
              <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <div className="flex items-center justify-center gap-4 text-2xl font-bold">
                  <div className="flex items-center gap-2">
                    <span className="text-blue-500">{reactant1Amount}H₂</span>
                  </div>
                  <Plus className="text-gray-400" />
                  <div className="flex items-center gap-2">
                    <span className="text-red-500">{reactant2Amount}O₂</span>
                  </div>
                  <span className="text-gray-400">→</span>
                  <div className={`transition-all duration-500 ${isReacting ? 'scale-125 text-green-500' : 'text-gray-500'}`}>
                    {Math.min(reactant1Amount * 2, reactant2Amount * 2)}H₂O
                    {isReacting && <Flame className="inline ml-2 text-orange-500 animate-pulse" />}
                  </div>
                </div>
                <div className={`text-center mt-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  2H₂ + O₂ → 2H₂O (Balanced Equation)
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  H₂ (Hydrogen): {reactant1Amount} moles
                </label>
                <input
                  type="range"
                  min="0"
                  max="10"
                  step="0.5"
                  value={reactant1Amount}
                  onChange={(e) => {
                    const newValue = Number(e.target.value);
                    setReactant1Amount(newValue);
                  }}
                  className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gradient-to-r from-blue-400 to-blue-600"
                  style={{ WebkitAppearance: 'none', outline: 'none' }}
                />
                <div className="flex justify-between text-xs mt-1">
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>0</span>
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>10 moles</span>
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  O₂ (Oxygen): {reactant2Amount} moles
                </label>
                <input
                  type="range"
                  min="0"
                  max="10"
                  step="0.5"
                  value={reactant2Amount}
                  onChange={(e) => {
                    const newValue = Number(e.target.value);
                    setReactant2Amount(newValue);
                  }}
                  className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gradient-to-r from-red-400 to-red-600"
                  style={{ WebkitAppearance: 'none', outline: 'none' }}
                />
                <div className="flex justify-between text-xs mt-1">
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>0</span>
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>10 moles</span>
                </div>
              </div>

              <button
                onClick={simulateReaction}
                disabled={isReacting}
                className={`w-full py-3 rounded-lg font-semibold transition-all ${
                  isReacting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : darkMode
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
              >
                {isReacting ? 'Reacting...' : 'Start Reaction'}
              </button>

              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-blue-50'}`}>
                <h4 className={`font-semibold mb-2 ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                  💡 Stoichiometry
                </h4>
                <ul className={`text-sm space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <li>• Reactants combine in fixed ratios (2:1 here)</li>
                  <li>• Limiting reactant determines product amount</li>
                  <li>• Mass is conserved in all chemical reactions</li>
                  <li>• Coefficients show molar ratios</li>
                </ul>
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  const getSimulationTitle = () => {
    switch (type) {
      case 'ph_scale':
        return 'pH Scale & Acidity';
      case 'temperature':
        return 'Temperature & Reaction Kinetics';
      case 'molarity':
        return 'Solution Concentration (Molarity)';
      case 'reaction':
        return 'Chemical Reaction Stoichiometry';
      default:
        return 'Chemistry Simulation';
    }
  };

  return (
    <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-lg`}>
      <div className="mb-4 flex justify-between items-start">
        <div>
          <h3 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            {getSimulationTitle()}
          </h3>
        </div>
        <button
          onClick={reset}
          className={`p-2 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} transition-all`}
          title="Reset"
        >
          <RotateCcw className="w-5 h-5" />
        </button>
      </div>

      {getSimulationContent()}
    </div>
  );
};

export default ChemistrySimulation;
